import { CounterDeltas, GetSampleCounterResponse, Roi, RoiChange, unlabeled as UNLABELED } from '../../entities';
import { LabelChanges } from '../labeling-service';

export interface MergeChangesInput {
  /** Changes to fold in order; the accumulator starts empty. */
  changes: LabelChanges[];
  /** True when the fold represents a brand-new ROI (removed labels never accumulate in that case). */
  isNew?: boolean;
}

export class ChangeProjectionService {
  public static applyChanges(roi: Roi, changes: LabelChanges): Roi {
    const newLabels = new Map(roi.labels);

    for (const label of changes.labelsAdded) newLabels.set(label, 1);
    for (const label of changes.labelsRemoved) newLabels.delete(label);

    return {
      ...roi,
      labels: newLabels,
      additionalLabelsIncluded: newLabels.size > 0 ? roi.additionalLabelsIncluded : true,
    };
  }

  /**
   * Folds a sequence of label changes into a single net diff from the server baseline.
   * Returns null when the changes cancel out to baseline (caller can drop the change record).
   *
   * @param changes - Changes to fold in order; empty array yields null.
   * @param isNew   - True when the fold represents a brand-new ROI whose baseline is empty
   *                  (removed labels never accumulate in that case).
   */
  public static mergeChanges({ changes, isNew = false }: MergeChangesInput): LabelChanges | null {
    if (changes.length === 0) return null;

    let added = new Set<string>();
    let removed = new Set<string>();

    for (const change of changes) {
      const nextAdded = new Set(added);
      const nextRemoved = new Set(removed);

      for (const label of change.labelsAdded) {
        if (removed.has(label)) continue;
        nextAdded.add(label);
      }
      for (const label of change.labelsRemoved) {
        if (added.has(label)) continue;
        nextRemoved.add(label);
      }

      added = new Set([...nextAdded].filter((l) => !change.labelsRemoved.includes(l)));
      removed = isNew ? new Set() : new Set([...nextRemoved].filter((l) => !change.labelsAdded.includes(l)));
    }

    const labelsAdded = Array.from(added);
    const labelsRemoved = Array.from(removed);

    if (!isNew && labelsAdded.length === 0 && labelsRemoved.length === 0) return null;

    return { labelsAdded, labelsRemoved };
  }

  public static projectChanges(baseline: Map<string, Roi>, changes: RoiChange[]): Map<string, Roi> {
    const result = new Map(baseline);

    for (const change of changes) {
      if (change.isNew && change.snapshot) {
        result.set(change.id, change.snapshot);
        continue;
      }

      const roi = result.get(change.id);
      if (!roi) continue;

      result.set(change.id, this.applyChanges(roi, change));
    }

    return result;
  }

  public static computeCounterDeltas(
    previousLabels: string[],
    currentLabels: string[],
    labelsAdded: string[],
    labelsRemoved: string[]
  ): CounterDeltas {
    const labelCountDeltas = new Map<string, number>();
    const singleLabelDeltas = new Map<string, number>();
    const multiLabelDeltas = new Map<string, number>();

    // Per-label deltas
    for (const label of labelsAdded) labelCountDeltas.set(label, (labelCountDeltas.get(label) ?? 0) + 1);
    for (const label of labelsRemoved) labelCountDeltas.set(label, (labelCountDeltas.get(label) ?? 0) - 1);

    // Unlabeled transitions
    const wasUnlabeled = previousLabels.length === 0;
    const isUnlabeled = currentLabels.length === 0;
    if (isUnlabeled && !wasUnlabeled) labelCountDeltas.set(UNLABELED, (labelCountDeltas.get(UNLABELED) ?? 0) + 1);
    else if (!isUnlabeled && wasUnlabeled) labelCountDeltas.set(UNLABELED, (labelCountDeltas.get(UNLABELED) ?? 0) - 1);

    // Single/multi bucket deltas
    const prevSingle = previousLabels.length === 1 ? previousLabels[0] : null;
    const prevCombo = previousLabels.length > 1 ? [...previousLabels].sort().join('/') : null;
    const newSingle = currentLabels.length === 1 ? currentLabels[0] : null;
    const newCombo = currentLabels.length > 1 ? [...currentLabels].sort().join('/') : null;

    if (prevSingle) singleLabelDeltas.set(prevSingle, (singleLabelDeltas.get(prevSingle) ?? 0) - 1);
    else if (prevCombo) multiLabelDeltas.set(prevCombo, (multiLabelDeltas.get(prevCombo) ?? 0) - 1);
    if (newSingle) singleLabelDeltas.set(newSingle, (singleLabelDeltas.get(newSingle) ?? 0) + 1);
    else if (newCombo) multiLabelDeltas.set(newCombo, (multiLabelDeltas.get(newCombo) ?? 0) + 1);

    return { labelCountDeltas, singleLabelDeltas, multiLabelDeltas };
  }

  // Delta for an ROI that is being deleted from the universe entirely
  // (e.g. a brand-new ROI removed via revert). Decrements the label/single/multi
  // buckets it contributed to, and the unlabeled bucket only if it had no labels.
  // Never emits +UNLABELED — the ROI is gone, not transitioning to unlabeled.
  public static computeRoiDeletionDelta(previousLabels: string[]): CounterDeltas {
    const labelCountDeltas = new Map<string, number>();
    const singleLabelDeltas = new Map<string, number>();
    const multiLabelDeltas = new Map<string, number>();

    if (previousLabels.length === 0) {
      labelCountDeltas.set(UNLABELED, -1);
      return { labelCountDeltas, singleLabelDeltas, multiLabelDeltas };
    }

    for (const label of previousLabels) labelCountDeltas.set(label, -1);
    if (previousLabels.length === 1) {
      singleLabelDeltas.set(previousLabels[0], -1);
    } else {
      multiLabelDeltas.set([...previousLabels].sort().join('/'), -1);
    }

    return { labelCountDeltas, singleLabelDeltas, multiLabelDeltas };
  }

  public static mergeCounterDeltas(deltas: CounterDeltas[]): CounterDeltas {
    const labelCountDeltas = new Map<string, number>();
    const singleLabelDeltas = new Map<string, number>();
    const multiLabelDeltas = new Map<string, number>();

    for (const d of deltas) {
      for (const [k, v] of d.labelCountDeltas) labelCountDeltas.set(k, (labelCountDeltas.get(k) ?? 0) + v);
      for (const [k, v] of d.singleLabelDeltas) singleLabelDeltas.set(k, (singleLabelDeltas.get(k) ?? 0) + v);
      for (const [k, v] of d.multiLabelDeltas) multiLabelDeltas.set(k, (multiLabelDeltas.get(k) ?? 0) + v);
    }

    return { labelCountDeltas, singleLabelDeltas, multiLabelDeltas };
  }

  public static projectCounters(
    base: GetSampleCounterResponse,
    changes: RoiChange[],
    baselineRois: Map<string, Roi>
  ): GetSampleCounterResponse {
    const labelCounts = new Map(base.labelCounts);
    const singleLabelCount = new Map(base.singleLabelCount ?? new Map());
    const multiLabelCount = new Map(base.multiLabelCount ?? new Map());
    let totalCount = base.totalCount;

    for (const change of changes) {
      let previousLabels: string[];
      let currentLabels: string[];

      if (change.isNew && change.snapshot) {
        previousLabels = [];
        currentLabels = Array.from(change.snapshot.labels.keys());
        totalCount++;
      } else {
        const baseline = baselineRois.get(change.id);
        if (!baseline) continue;

        previousLabels = Array.from(baseline.labels.keys());
        const projected = this.applyChanges(baseline, change);
        currentLabels = Array.from(projected.labels.keys());

        if (currentLabels.length === 0 && previousLabels.length > 0) {
          totalCount--;
        }
      }

      const deltas = this.computeCounterDeltas(previousLabels, currentLabels, change.labelsAdded, change.labelsRemoved);

      for (const [k, v] of deltas.labelCountDeltas) labelCounts.set(k, Math.max((labelCounts.get(k) ?? 0) + v, 0));
      for (const [k, v] of deltas.singleLabelDeltas)
        singleLabelCount.set(k, Math.max((singleLabelCount.get(k) ?? 0) + v, 0));
      for (const [k, v] of deltas.multiLabelDeltas)
        multiLabelCount.set(k, Math.max((multiLabelCount.get(k) ?? 0) + v, 0));
    }

    return { totalCount, labelCounts, singleLabelCount, multiLabelCount };
  }
}
